/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

/**
 * matrix-js-sdk registers every `matrix-*` loglevel logger at DEBUG, which floods DevTools
 * (FetchHttpApi, sync, MatrixChat debug, etc.). This module must load before `matrix-js-sdk/src/browser-index`.
 *
 * - Default: cap matrix loggers at WARN (only warnings and errors on the console).
 * - Override: `?log=debug` (or trace|info|warn|error|silent) on first load, or set localStorage
 *   `mx_js_sdk_log_level` to the same token.
 */

import loglevel from "loglevel";

function parseLogLevel(raw: string | null | undefined): number | null {
    if (!raw) {
        return null;
    }
    const key = raw.toUpperCase() as keyof typeof loglevel.levels;
    if (key in loglevel.levels && typeof loglevel.levels[key] === "number") {
        return loglevel.levels[key] as number;
    }
    return null;
}

function getConfiguredMatrixSdkLogFloor(): number {
    if (typeof window === "undefined") {
        return loglevel.levels.WARN;
    }
    try {
        const fromQuery = parseLogLevel(new URLSearchParams(window.location.search).get("log"));
        if (fromQuery != null) {
            return fromQuery;
        }
    } catch {
        // ignore
    }
    try {
        const fromLs = parseLogLevel(window.localStorage?.getItem("mx_js_sdk_log_level"));
        if (fromLs != null) {
            return fromLs;
        }
    } catch {
        // ignore (e.g. private mode)
    }
    return loglevel.levels.WARN;
}

function normalizeLogLevelDesc(level: loglevel.LogLevelDesc): number {
    if (typeof level === "number") {
        return level;
    }
    if (typeof level === "string") {
        const parsed = parseLogLevel(level);
        if (parsed != null) {
            return parsed;
        }
    }
    return loglevel.levels.WARN;
}

const patchedLoggers = new WeakSet<object>();

const originalGetLogger = loglevel.getLogger.bind(loglevel) as typeof loglevel.getLogger;

loglevel.getLogger = (name: string): loglevel.Logger => {
    const logger = originalGetLogger(name);
    if (name !== "matrix" && !name.startsWith("matrix-")) {
        return logger;
    }
    if (patchedLoggers.has(logger)) {
        return logger;
    }
    patchedLoggers.add(logger);

    const originalSetLevel = logger.setLevel.bind(logger);
    (logger as loglevel.Logger & { setLevel: typeof logger.setLevel }).setLevel = (
        level: loglevel.LogLevelDesc,
        persist?: boolean,
    ): unknown => {
        const requested = normalizeLogLevelDesc(level);
        const floor = getConfiguredMatrixSdkLogFloor();
        const effective = Math.max(requested, floor);
        return originalSetLevel(effective, persist);
    };
    return logger;
};
